import type { IntakeCore } from '@shared/types';
import type { MaterialKind } from '@shared/spec';
import { DimensionsInput } from '@/components/intake/fields/DimensionsInput';
import { ChipSelect } from '@/components/intake/fields/ChipSelect';
import { useTranslation } from 'react-i18next';

const MATERIAL_OPTIONS: Array<{ value: MaterialKind; label: string }> = [
  { value: 'plywood', label: '合板' },
  { value: 'hardwood', label: 'ハードウッド' },
  { value: 'softwood', label: 'ソフトウッド' },
  { value: 'mdf', label: 'MDF' },
  { value: 'sheet_metal', label: 'スチール板' },
  { value: 'printed_pla', label: 'PLA' },
  { value: 'printed_abs', label: 'ABS' },
  { value: 'printed_petg', label: 'PETG' },
  { value: 'custom', label: 'カスタム' },
];

const FASTENER_OPTIONS: Array<{ value: NonNullable<IntakeCore['fastenerPreference']>; label: string }> = [
  { value: 'screws', label: 'ネジ' },
  { value: 'hidden', label: '隠し' },
  { value: 'glue_only', label: '接着のみ' },
  { value: 'mortise_tenon', label: 'ほぞ継ぎ' },
  { value: 'none', label: 'なし' },
  { value: 'mixed', label: '混在' },
];

export function CoreSection({
  value,
  hideIntendedUse = false,
  onChange,
}: {
  value: IntakeCore | undefined;
  hideIntendedUse?: boolean;
  onChange: (next: IntakeCore) => void;
}) {
  const { t } = useTranslation('intake');
  const v: IntakeCore = value ?? { intendedUse: '' };
  const update = (next: Partial<IntakeCore>) => onChange({ ...v, ...next });

  return (
    <div className="flex flex-col gap-3">
      {!hideIntendedUse && (
        <div>
          <label className="text-[10px] text-synergy-text-tertiary">{t('coreLabels.intendedUse')}</label>
          <input
            type="text"
            value={v.intendedUse}
            onChange={(e) => update({ intendedUse: e.target.value })}
            maxLength={120}
            placeholder={t('coreLabels.intendedUsePlaceholder')}
            className="mt-1 w-full rounded-md border border-synergy-neutral-700/40 bg-white/60 px-2 py-1.5 text-sm"
          />
        </div>
      )}
      <div>
        <label className="text-[10px] text-synergy-text-tertiary">{t('coreLabels.size')}</label>
        <div className="mt-1">
          <DimensionsInput value={v.size} onChange={(size) => update({ size })} />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-synergy-text-tertiary">{t('coreLabels.material')}</label>
        <div className="mt-1">
          <ChipSelect
            options={MATERIAL_OPTIONS}
            value={v.primaryMaterial}
            onChange={(primaryMaterial) => update({ primaryMaterial })}
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-synergy-text-tertiary">{t('coreLabels.fastenerPreference')}</label>
        <div className="mt-1">
          <ChipSelect
            options={FASTENER_OPTIONS}
            value={v.fastenerPreference}
            onChange={(fastenerPreference) => update({ fastenerPreference })}
          />
        </div>
      </div>
    </div>
  );
}
